/* eslint-disable react-hooks/rules-of-hooks, react-hooks/exhaustive-deps */
import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import CreatableSelect from 'react-select/creatable';

// TODO: update styles
import styles from '../../styles/views/components/Dropdown.module.scss';

const dropdownFont = {
  fontSize: '16px',
  color: styles.colorAztec,
  fontFamily: styles.fontFamilyMain,
};
const types = {
  common: Select,
  async: AsyncSelect,
  creatable: CreatableSelect,
};

const DropdownV2 = ({
  componentsStyles,
  classNameOfContainer,
  type,
  name,
  label,
  placeholder,
  isClearable,
  isSearchable,
  isDisabled,
  isLoading,
  isRtl,
  isMulti,
  primitiveValue,
  options,
  optionValue,
  optionLabel,
  optionDisabled,
  createdOption,
  onChange,
  onInputChange,
  onCreateOption,
  onMenuScrollToBottom,
  onFocus,
  error,
  customComponents,
  hideSelectedOptions,
  closeMenuOnSelect,
  tooltip,
}) => {
  const Component = types[type];

  if (!Component) return null;
  const formattedMemoizedOptions = useMemo(() => options.map(item => ({ value: item[optionValue], label: item[optionLabel], isDisabled: item[optionDisabled] })), [options]);
  const formattedMemoizedOption = useMemo(() => {
    if (!primitiveValue && primitiveValue !== 0) return null;
    if (createdOption) return { value: createdOption[optionValue], label: createdOption[optionLabel] };
    if (isMulti) {
      const result = options.filter(option => primitiveValue.some(item => item === option[optionValue]));
      return result.map(r => ({ value: r[optionValue], label: r[optionLabel] }));
    }
    const foundItem = options.find(item => item[optionValue] === primitiveValue);
    if (!foundItem) return null;
    return { value: foundItem[optionValue], label: foundItem[optionLabel] };
  }, [options, primitiveValue, createdOption]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const stylesConfig = {
    container: defaultStyles => ({
      ...defaultStyles,
      width: '100%',
      marginTop: label ? '-11px' : 0,
      padding: '0',
      height: isMulti ? 'auto' : null,
    }),
    control: (defaultStyles, state) => ({
      ...defaultStyles,
      ...dropdownFont,
      width: '100%',
      display: 'flex',
      backgroundColor: !state.isDisabled ? styles.colorWhite : styles.colorAlabaster,
      border: 'none',
      boxSizing: 'border-box',
      boxShadow: 'none',
      minHeight: '36px',
      height: isMulti ? 'auto' : '36px',
      '&:hover': {
        boxShadow: 'none',
      },
      borderRadius: 0,
    }),
    placeholder: defaultStyles => ({
      ...defaultStyles,
      ...dropdownFont,
      color: error ? styles.colorRed : (isSearchable ? styles.colorAlto : styles.colorAztec),
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      ...componentsStyles?.placeholder,
    }),
    menu: defaultStyles => ({
      ...defaultStyles,
      borderRadius: 0,
      boxSizing: 'content-box',
      boxShadow: '0px 5px 5px rgba(0, 0, 0, 0.05)',
      border: `1px solid ${styles.colorAztec}`,
      width: '100%',
      margin: '0 0 0 -1px',
      zIndex: 2,
    }),
    menuList: defaultStyles => ({
      ...defaultStyles,
      padding: 0,
      maxHeight: '120px',
      '&::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: styles.colorAlabaster,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: styles.colorAlto,
      },
      ...componentsStyles?.menuList,
    }),
    option: (defaultStyles, state) => ({
      ...defaultStyles,
      ...dropdownFont,
      backgroundColor: state.isFocused ? styles.colorAlabaster : styles.colorWhite,
      padding: '10px 11px',
      color: state.isDisabled ? styles.colorDustyGray : styles.colorAztec,
      ...componentsStyles?.option,
    }),
    dropdownIndicator: (defaultStyles, state) => ({
      ...defaultStyles,
      color: !state.isDisabled ? styles.colorAztec : styles.colorAlto,
      transition: 'all .2s ease',
      transform: isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    }),
    singleValue: defaultStyles => ({
      ...defaultStyles,
      ...dropdownFont,
      ...componentsStyles?.singleValue,
    }),
  };
  const classNameOfFieldset = (function () {
    const result = [];
    if (!label) result.push(styles.noLabel);
    if (isDisabled) result.push(styles.disabled);
    if (error) result.push(styles.error);
    if (isMulti) result.push(styles.isMulti);
    return result.join(' ');
  })();

  const commonProps = {
    styles: stylesConfig,
    inputId: name,
    name,
    placeholder,
    isClearable,
    isSearchable,
    isDisabled,
    isLoading,
    isRtl,
    isMulti,
    value: formattedMemoizedOption,
    options: formattedMemoizedOptions,
    onChange,
    onInputChange,
    onCreateOption,
    onMenuScrollToBottom,
    onFocus,
    hideSelectedOptions,
    closeMenuOnSelect,
    onMenuOpen: () => setIsMenuOpen(true),
    onMenuClose: () => setIsMenuOpen(false),
    components: { IndicatorSeparator: () => null, ...customComponents },
  };

  return (
    <div data-tip={tooltip} className={[styles.container, classNameOfContainer, isMulti && styles.isMulti].join(' ')}>
      <fieldset className={classNameOfFieldset}>
        {label && <legend>{label}</legend>}
        <Component {...commonProps} />
      </fieldset>
      <div className={styles.error}>
        {error ? (
          <div className={styles.message}>
            {error}
          </div>
        ) : null}
      </div>
    </div>
  );
};

DropdownV2.propTypes = {
  componentsStyles: PropTypes.object,
  classNameOfContainer: PropTypes.string,
  type: PropTypes.oneOf(['common', 'async', 'creatable']),
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  isClearable: PropTypes.bool,
  isSearchable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  isRtl: PropTypes.bool,
  isMulti: PropTypes.bool,
  primitiveValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.number,
  ]),
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  optionValue: PropTypes.string.isRequired,
  optionLabel: PropTypes.string.isRequired,
  optionDisabled: PropTypes.string,
  createdOption: PropTypes.object,
  onChange: PropTypes.func,
  onInputChange: PropTypes.func,
  onCreateOption: PropTypes.func,
  onMenuScrollToBottom: PropTypes.func,
  onFocus: PropTypes.func,
  error: PropTypes.string,
  customComponents: PropTypes.object,
  hideSelectedOptions: PropTypes.bool,
  closeMenuOnSelect: PropTypes.bool,
  tooltip: PropTypes.string,
};

DropdownV2.defaultProps = {
  componentsStyles: {},
  classNameOfContainer: '',
  type: 'common',
  label: '',
  placeholder: '',
  isClearable: false,
  isSearchable: false,
  isDisabled: false,
  isLoading: false,
  isRtl: false,
  isMulti: false,
  primitiveValue: null,
  optionDisabled: '',
  createdOption: null,
  onChange: () => {},
  onInputChange: () => {},
  onCreateOption: () => {},
  onMenuScrollToBottom: () => {},
  onFocus: () => {},
  error: '',
  customComponents: {},
  hideSelectedOptions: false,
  closeMenuOnSelect: true,
  tooltip: '',
};

export default DropdownV2;
